import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft, Save, Trash2, AlertTriangle } from 'lucide-react'
import api from '../../services/api'

const RATING_COLOURS: Record<string, { bg: string; text: string }> = {
  critical: { bg: '#FEF2F2', text: '#DC2626' },
  high:     { bg: '#FFF7ED', text: '#EA580C' },
  medium:   { bg: '#FEFCE8', text: '#CA8A04' },
  low:      { bg: '#F0FDF4', text: '#16A34A' },
}

const LIKELIHOOD_LABELS = ['','Rare','Unlikely','Possible','Likely','Almost Certain']
const CONSEQUENCE_LABELS = ['','Insignificant','Minor','Moderate','Major','Catastrophic']
const CATEGORIES = ['operational','health_safety','environmental','financial','reputational','compliance','strategic']

function riskRating(l: number, c: number) {
  const s = l * c
  if (s >= 17) return 'critical'
  if (s >= 10) return 'high'
  if (s >= 5)  return 'medium'
  return 'low'
}

function RiskMatrix({ likelihood, consequence, onChange }: { likelihood: number; consequence: number; onChange: (l: number, c: number) => void }) {
  return (
    <div>
      <div className="grid gap-1" style={{ gridTemplateColumns: 'auto repeat(5, 1fr)', fontSize: 11 }}>
        {/* Header row */}
        <div />
        {CONSEQUENCE_LABELS.slice(1).map((l, i) => (
          <div key={i} className="text-center font-semibold py-1" style={{ color: 'var(--text-muted)' }}>{l}</div>
        ))}
        {/* Matrix rows (likelihood 5→1) */}
        {[5,4,3,2,1].map(l => (
          <>
            <div key={`lbl-${l}`} className="flex items-center justify-end pr-2 font-semibold" style={{ color: 'var(--text-muted)' }}>
              {LIKELIHOOD_LABELS[l]}
            </div>
            {[1,2,3,4,5].map(c => {
              const rating = riskRating(l, c)
              const colours: Record<string,string> = { critical:'#FCA5A5', high:'#FDBA74', medium:'#FEF08A', low:'#BBF7D0' }
              const isSelected = l === likelihood && c === consequence
              return (
                <button key={`${l}-${c}`}
                  onClick={() => onChange(l, c)}
                  style={{
                    background: colours[rating],
                    border: isSelected ? '3px solid #1B2B4B' : '1px solid rgba(0,0,0,0.1)',
                    borderRadius: 6, height: 36, cursor: 'pointer',
                    fontWeight: isSelected ? 900 : 400,
                    fontSize: isSelected ? 12 : 10,
                  }}>
                  {isSelected ? `${l}×${c}` : l * c}
                </button>
              )
            })}
          </>
        ))}
      </div>
      <div className="flex gap-3 mt-2 text-xs justify-center">
        {Object.entries({ low:'#BBF7D0', medium:'#FEF08A', high:'#FDBA74', critical:'#FCA5A5' }).map(([r, bg]) => (
          <span key={r} className="flex items-center gap-1">
            <span style={{ width: 12, height: 12, background: bg, borderRadius: 3, display: 'inline-block' }} />
            <span className="capitalize font-medium" style={{ color: 'var(--text-muted)' }}>{r}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

export default function RiskDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const [form, setForm] = useState<any>({
    title: '', description: '', category: 'operational', hazard_source: '',
    department: '', existing_controls: '', additional_controls: '',
    likelihood: 3, consequence: 3, residual_likelihood: 2, residual_consequence: 2,
    owner_id: '', review_date: '', status: 'open',
  })
  const [users, setUsers] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(!isNew)

  useEffect(() => {
    api.get('/users?limit=100').then(r => setUsers(r.data.data || r.data)).catch(() => {})
    if (!isNew) {
      api.get(`/risk-register/${id}`).then(r => { setForm(r.data); setLoading(false) }).catch(() => navigate('/risk-register'))
    }
  }, [id])

  const f = (key: string, val: any) => setForm((p: any) => ({ ...p, [key]: val }))

  const save = async () => {
    if (!form.title) return
    setSaving(true)
    try {
      if (isNew) {
        const { data } = await api.post('/risk-register', form)
        navigate(`/risk-register/${data.id}`, { replace: true })
      } else {
        await api.patch(`/risk-register/${id}`, form)
      }
    } finally { setSaving(false) }
  }

  const del = async () => {
    if (!confirm('Delete this risk?')) return
    await api.delete(`/risk-register/${id}`)
    navigate('/risk-register')
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)' }} /></div>

  const inherentRating = riskRating(form.likelihood, form.consequence)
  const residualRating  = riskRating(form.residual_likelihood, form.residual_consequence)
  const ic = RATING_COLOURS[inherentRating]
  const rc = RATING_COLOURS[residualRating]

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/risk-register')} className="p-2 rounded-lg hover:bg-gray-100">
            <ChevronLeft size={20} style={{ color: 'var(--text-muted)' }} />
          </button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--text)' }}>{isNew ? 'New Risk' : form.title}</h1>
            {!isNew && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{form.risk_number}</p>}
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && (
            <button onClick={del} className="btn btn-ghost text-red-600"><Trash2 size={16} /> Delete</button>
          )}
          <button onClick={save} disabled={saving || !form.title} className="btn btn-primary">
            <Save size={16} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Rating summary */}
      {!isNew && (
        <div className="grid grid-cols-2 gap-4">
          <div className="card text-center" style={{ borderColor: ic.text }}>
            <p className="text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Inherent Risk</p>
            <p className="text-3xl font-extrabold mt-1" style={{ color: ic.text }}>{inherentRating.toUpperCase()}</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{form.likelihood} × {form.consequence} = {form.likelihood * form.consequence}</p>
          </div>
          <div className="card text-center" style={{ borderColor: rc.text }}>
            <p className="text-xs font-semibold uppercase" style={{ color: 'var(--text-muted)' }}>Residual Risk</p>
            <p className="text-3xl font-extrabold mt-1" style={{ color: rc.text }}>{residualRating.toUpperCase()}</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{form.residual_likelihood} × {form.residual_consequence} = {form.residual_likelihood * form.residual_consequence}</p>
          </div>
        </div>
      )}

      {/* Basic info */}
      <div className="card space-y-4">
        <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Risk Details</h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Title *</label>
            <input className="input" value={form.title} onChange={e => f('title', e.target.value)} placeholder="Brief risk description" />
          </div>
          <div>
            <label className="label">Category</label>
            <select className="input" value={form.category} onChange={e => f('category', e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g,' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input" value={form.status} onChange={e => f('status', e.target.value)}>
              <option value="open">Open</option>
              <option value="in_treatment">In Treatment</option>
              <option value="accepted">Accepted</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div>
            <label className="label">Department</label>
            <input className="input" value={form.department || ''} onChange={e => f('department', e.target.value)} placeholder="e.g. Operations" />
          </div>
          <div>
            <label className="label">Hazard Source</label>
            <input className="input" value={form.hazard_source || ''} onChange={e => f('hazard_source', e.target.value)} placeholder="e.g. Electrical equipment" />
          </div>
          <div>
            <label className="label">Risk Owner</label>
            <select className="input" value={form.owner_id || ''} onChange={e => f('owner_id', e.target.value)}>
              <option value="">Unassigned</option>
              {users.map((u: any) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Review Date</label>
            <input className="input" type="date" value={form.review_date || ''} onChange={e => f('review_date', e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label">Description</label>
            <textarea className="input" rows={3} value={form.description || ''} onChange={e => f('description', e.target.value)} placeholder="Describe the risk scenario…" />
          </div>
          <div className="col-span-2">
            <label className="label">Existing Controls</label>
            <textarea className="input" rows={3} value={form.existing_controls || ''} onChange={e => f('existing_controls', e.target.value)} placeholder="Controls already in place…" />
          </div>
          <div className="col-span-2">
            <label className="label">Additional Controls Required</label>
            <textarea className="input" rows={3} value={form.additional_controls || ''} onChange={e => f('additional_controls', e.target.value)} placeholder="Further actions needed to reduce risk…" />
          </div>
        </div>
      </div>

      {/* Inherent risk matrix */}
      <div className="card space-y-4">
        <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Inherent Risk (before controls)</h2>
        <RiskMatrix likelihood={form.likelihood} consequence={form.consequence}
          onChange={(l, c) => { f('likelihood', l); f('consequence', c) }} />
      </div>

      {/* Residual risk matrix */}
      <div className="card space-y-4">
        <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Residual Risk (after controls)</h2>
        <RiskMatrix likelihood={form.residual_likelihood} consequence={form.residual_consequence}
          onChange={(l, c) => { f('residual_likelihood', l); f('residual_consequence', c) }} />
      </div>
    </div>
  )
}
